/**
 * <product-recommendations> lazy-loads Shopify product recommendations.
 *
 * Public contract:
 *   data-url        - Shopify recommendations URL for the current section
 *   data-section-id - section id used to find the matching fetched section
 */

class ProductRecommendations extends HTMLElement {
  #observer = null;
  #isLoaded = false;

  connectedCallback() {
    if (this.#isLoaded || !this.dataset.url) return;

    if (!this.innerHTML.trim()) {
      this.#loadRecommendations();
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      rootMargin: '-100px 0px 200px 0px',
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleIntersection = (entries) => {
    if (!entries[0]?.isIntersecting) return;

    this.#observer?.disconnect();
    this.#observer = null;
    this.#loadRecommendations();
  };

  async #loadRecommendations() {
    this.#isLoaded = true;

    try {
      const response = await fetch(this.dataset.url);
      const text = await response.text();
      const html = document.createElement('div');

      html.innerHTML = text;

      const recommendations =
        html.querySelector(`product-recommendations[data-section-id="${this.dataset.sectionId}"]`) ||
        html.querySelector('product-recommendations');

      if (!recommendations?.innerHTML.trim()) {
        this.remove();
        return;
      }

      this.innerHTML = recommendations.innerHTML;
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
